import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FolderPlus, X, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const STORAGE_KEY = 'pendingCollectionPrompt';
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h — matches the pendingClaims TTL

interface ClaimedFile {
  id: string;
  name: string;
}

interface PendingCollectionPrompt {
  timestamp: number;
  files: ClaimedFile[];
}

/**
 * Derive a sensible collection name from an uploaded filename: drop the
 * extension, turn separators into spaces, collapse whitespace. We deliberately
 * keep the user's original casing (Postel's Law — clean the input but don't
 * over-transform it); they review and can edit it on the next screen.
 */
export const cleanCollectionName = (filename: string): string => {
  const withoutExt = filename.replace(/\.[^/.]+$/, '');
  const cleaned = withoutExt
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 100);
  return cleaned || 'New collection';
};

/**
 * Owns the post-claim prompt's visibility. Reads the localStorage key written
 * by AuthContext.processPendingClaims and exposes the staged files (or null)
 * plus a clear() to consume/dismiss it.
 *
 * Lifted out of the component so Home can read the same single source of truth
 * — it needs to know whether the prompt is showing in order to suppress the
 * competing "Upgrade to Pro" hero (one primary nudge at a time).
 */
export const useClaimedFilesPrompt = (): {
  prompt: PendingCollectionPrompt | null;
  clear: () => void;
} => {
  const { isAuthenticated } = useAuth();
  const [prompt, setPrompt] = useState<PendingCollectionPrompt | null>(null);

  const read = useCallback(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      setPrompt(null);
      return;
    }
    try {
      const parsed: PendingCollectionPrompt = JSON.parse(stored);
      const fresh = Date.now() - parsed.timestamp < MAX_AGE_MS;
      if (fresh && Array.isArray(parsed.files) && parsed.files.length >= 2) {
        setPrompt(parsed);
      } else {
        // Stale or too few files to be worth a collection — drop it.
        localStorage.removeItem(STORAGE_KEY);
        setPrompt(null);
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      setPrompt(null);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Read once now — covers the page-reload sign-in path, where the key is
    // already in storage by the time we mount. Also listen for the write
    // signal: on the no-reload sign-in path the key lands AFTER isAuthenticated
    // flips true, so a one-shot read would miss it.
    read();
    window.addEventListener('pendingcollectionprompt', read);
    return () => window.removeEventListener('pendingcollectionprompt', read);
  }, [isAuthenticated, read]);

  const clear = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setPrompt(null);
  }, []);

  return { prompt, clear };
};

interface ClaimedFilesCollectionPromptProps {
  files: ClaimedFile[];
  /** Consume/dismiss the prompt (clears the localStorage key + parent state). */
  onClear: () => void;
}

/**
 * Post-claim nudge: after an anonymous uploader signs in and 2+ of their files
 * are re-claimed, offer to group them into a collection. Routes the user to
 * /collections with the create form pre-filled (name + staged file IDs) so they
 * confirm creation there and land on the populated collection.
 *
 * Presentational — visibility is owned by useClaimedFilesPrompt() in the parent
 * (Home), which also uses it to decide whether to show the upgrade hero. Inline
 * box, never a modal. Home-page only, above Recent Files where the claimed
 * files highlight.
 */
export const ClaimedFilesCollectionPrompt: React.FC<ClaimedFilesCollectionPromptProps> = ({
  files,
  onClear,
}) => {
  const navigate = useNavigate();

  const handleSetUp = () => {
    const fileIds = files.map((f) => f.id);
    const name = cleanCollectionName(files[0].name);
    onClear();
    navigate('/collections', { state: { prefillCollection: { name, fileIds } } });
  };

  const count = files.length;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -12 }}
        transition={{ duration: 0.3 }}
        className="mb-6 rounded-xl border border-soundwave-500/30 bg-soundwave-500/10 p-4"
      >
        <div className="flex items-start gap-3">
          {/* Icon */}
          <FolderPlus className="h-5 w-5 flex-shrink-0 text-soundwave-400 mt-0.5" />

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm">
              Organize your <span className="font-semibold">{count} saved files</span>?
            </p>
            <p className="text-white/60 text-xs mt-1">
              Turn them into a shareable collection — one link for the whole set.
            </p>

            <div className="mt-3 flex items-center gap-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSetUp}
                className="inline-flex items-center gap-2 rounded-lg bg-soundwave-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-soundwave-600"
              >
                Set up a collection
                <ArrowRight className="h-4 w-4" />
              </motion.button>
              <button
                onClick={onClear}
                className="text-sm text-white/50 transition-colors hover:text-white/70"
              >
                Not now
              </button>
            </div>
          </div>

          {/* Dismiss Button */}
          <button
            onClick={onClear}
            className="flex-shrink-0 rounded p-1 text-soundwave-400 transition-colors hover:bg-soundwave-500/20 hover:text-soundwave-300"
            aria-label="Dismiss collection prompt"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ClaimedFilesCollectionPrompt;
